import { useComplexQuery } from '@sequencehq/api'
import {
  Badge,
  Button,
  GreyGrey60,
  Image,
  PageTitle,
  Primary13Regular,
  SequenceMenu,
  Skeleton,
  StandardLayout,
  Tooltip,
  Flex
} from '@sequencehq/core-components'
import { toBillingScheduleStatusBadgeProps } from '@sequencehq/utils'
import {
  fetchBillingScheduleAggregate,
  getBillingScheduleAggregateKey
} from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import {
  ScheduleAmendmentLockMessage,
  useScheduleAmendmentLock
} from 'common/components/ScheduleAmendmentLock'
import { ArchiveScheduleMenuItem } from 'common/menuItems/schedules/ArchiveScheduleMenuItem'
import { DuplicateScheduleMenuItem } from 'common/menuItems/schedules/DuplicateScheduleMenuItem'
import { getLogoSrc } from 'Customer/index'
import { useNavigate } from 'react-router-dom'
import { EditScheduleShortcutsModal } from './BillingScheduleHomeContent/modals/editScheduleShortcuts/EditScheduleShortcutsModal'
import Spinner from 'components/Loading'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { useState } from 'react'

const useBillingScheduleHomeHeader = () => {
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useComplexQuery({
    queryFn: () => fetchBillingScheduleAggregate(billingScheduleId),
    queryKey: getBillingScheduleAggregateKey(billingScheduleId),
    staleTime: 60000,
    select: data => {
      return {
        status: data.billingSchedule.status,
        customerId: data.billingSchedule.customerId,
        customerDomain: data.customer?.domain,
        archivedAt: data.billingSchedule.archivedAt,
        lockedByQuoteId: data.billingSchedule.lockedByQuoteId
      }
    }
  })

  const isArchived = Boolean(billingScheduleAggregateQuery.data?.archivedAt)
  const amendmentLock = useScheduleAmendmentLock(
    billingScheduleAggregateQuery.data?.lockedByQuoteId
  )

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    isRefreshing: billingScheduleAggregateQuery.isFetching,
    isArchived,
    billingScheduleId,
    billingScheduleStatus: billingScheduleAggregateQuery.data?.status,
    canArchive: !billingScheduleAggregateQuery.data?.archivedAt,
    amendmentLock,
    canUpdate:
      !isArchived &&
      billingScheduleAggregateQuery.data?.status &&
      ['DRAFT', 'ACTIVE', 'PENDING'].includes(
        billingScheduleAggregateQuery.data?.status
      ),
    customerLogoSrc: getLogoSrc({
      domain: billingScheduleAggregateQuery.data?.customerDomain
    }),
    customerId: billingScheduleAggregateQuery.data?.customerId
  }
}

export const BillingScheduleHomeHeader = () => {
  const {
    billingScheduleId,
    billingScheduleStatus,
    customerLogoSrc,
    isLoading,
    isRefreshing,
    canArchive,
    isArchived,
    canUpdate,
    amendmentLock
  } = useBillingScheduleHomeHeader()
  const navigate = useNavigate()

  const { can } = usePermissions()

  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false)

  return (
    <StandardLayout.Header
      title={
        isLoading ? (
          <Skeleton height="16px" width="280px" />
        ) : (
          <Flex
            data-testid="schedule.header"
            {...Primary13Regular}
            color={GreyGrey60}
            isTruncated
            display="flex"
            alignItems="center"
            gap="8px"
          >
            <PageTitle title={['Billing schedule', 'Schedules']} />
            {customerLogoSrc && (
              <Image src={customerLogoSrc} height="16px" width="16px" />
            )}
            <StandardLayout.Breadcrumbs>
              <StandardLayout.Breadcrumbs.Breadcrumb href="/billing-schedules">
                Schedules
              </StandardLayout.Breadcrumbs.Breadcrumb>
              <StandardLayout.Breadcrumbs.Breadcrumb>
                Billing schedule
              </StandardLayout.Breadcrumbs.Breadcrumb>
            </StandardLayout.Breadcrumbs>
            {billingScheduleStatus && (
              <Badge
                {...toBillingScheduleStatusBadgeProps({
                  status: billingScheduleStatus
                })}
              />
            )}
            {isArchived && (
              <Badge sentiment="neutral" size="md">
                Archived
              </Badge>
            )}
            {isRefreshing && (
              <Spinner
                height="16px"
                width="16px"
                color={GreyGrey60}
                data-testid="schedule.loading"
              />
            )}
          </Flex>
        )
      }
      actions={
        <Flex gap="8px" alignItems="center">
          {canUpdate && (
            <>
              {/* The tooltip sits outside ProtectedButton, which clones its own
                  child to deny permission and would otherwise clone the
                  tooltip rather than the button. */}
              <Tooltip
                isInteractive
                isDisabled={!amendmentLock.isLocked}
                placement="bottom"
                data-testid="schedule.edit.lockedTooltip"
                label={
                  <ScheduleAmendmentLockMessage
                    action="edited"
                    lock={amendmentLock}
                    data-testid="schedule.edit.lockedTooltip.message"
                  />
                }
              >
                <ProtectedButton permission="org.billing:update">
                  <Button
                    data-testid="schedule.edit"
                    variant="primary"
                    disabled={amendmentLock.isLocked}
                    onClick={() => setShowEditScheduleModal(true)}
                  >
                    Edit schedule
                  </Button>
                </ProtectedButton>
              </Tooltip>
              {showEditScheduleModal && (
                <EditScheduleShortcutsModal
                  onClose={() => setShowEditScheduleModal(false)}
                />
              )}
            </>
          )}
          {(can('org.billing:archive') || can('org.billing:create')) && (
            <SequenceMenu
              data-testid={`schedule.${billingScheduleId}.menu`}
              buttonSize="medium"
              alignment="bottom-right"
              variant="ghost"
              items={[
                <DuplicateScheduleMenuItem
                  key="duplicate-schedule"
                  scheduleId={billingScheduleId}
                  data-testid={`schedule.${billingScheduleId}.menuItem.duplicate`}
                  onSuccess={(newScheduleId: string) =>
                    navigate(`/billing-schedules/${newScheduleId}/edit`)
                  }
                />,
                ({ setIsOpen }) =>
                  canArchive && (
                    <ArchiveScheduleMenuItem
                      key="archive-schedule"
                      data-testid={`schedule.${billingScheduleId}.menuItem.archive`}
                      scheduleId={billingScheduleId}
                      lockedByQuoteId={amendmentLock.quoteId}
                      onSuccess={() => {
                        setIsOpen?.(false)
                      }}
                    />
                  )
              ]}
            />
          )}
        </Flex>
      }
    />
  )
}
